import { useCallback, useState } from "react";

/**
 * Hook for managing structure schema fields with required/optional field ordering.
 *
 * Maintains a list of fields where required fields always appear first, followed by
 * optional fields. Provides methods to dynamically add/remove optional fields while
 * preserving the required-first ordering constraint.
 *
 * @param fields - All available fields for the structure
 * @param requiredFields - Fields that must always be present and appear first
 *
 * @returns A tuple containing:
 *   - members: Current ordered list of fields (required first, then optional)
 *   - addFields: Function to add new optional fields after required fields
 *   - removeFields: Function to remove optional fields (required fields cannot be removed)
 */
export function useStructureFields(fields: string[], requiredFields: string[] | undefined) {
  const [members, setMembers] = useState<string[]>(() => [
    ...(requiredFields ?? []),
    ...fields.filter((f) => !requiredFields?.includes(f)),
  ]);

  const addFields = useCallback(
    (...fields: string[]) => {
      setMembers((prevMembers) => [...prevMembers, ...fields]);
    },
    [setMembers]
  );

  const removeFields = useCallback(
    (...fields: string[]) => {
      setMembers((prevMembers) => {
        const previousNonRequired = prevMembers.filter((p) => !requiredFields?.includes(p));
        const removed = previousNonRequired.filter((p) => !fields.includes(p));

        return [...(requiredFields ?? []), ...removed];
      });
    },
    [requiredFields, setMembers]
  );

  return [members, addFields, removeFields] as const;
}
